/**
 * Everything about a role that is not its title, in the space of two glyphs.
 *
 * THE PROBLEM THIS SOLVES is that the tracking table is about the TIMELINE —
 * was the seat filled, and was it filled on time — so the frozen column has to
 * stop eating the months. The demand board gave level, FTE, reason, backfill-for
 * and status a column each; here the level is a badge, the reason is one icon,
 * and whose seat a backfill replaces is on that icon's tooltip.
 *
 * THE HIRE'S NAME AND START DATE COST NOTHING because they ride the timeline:
 * the dot's column already IS the start month, so the name sits beside the dot
 * as a label on the event rather than as another column (Theresa's pick, Sep
 * 2026, option A). See HiringRow.
 *
 * The full picture — planned against actual, the level of the person being
 * replaced, the recruiter — is on the row's own hover card, which is the one
 * place both sides of a seat are visible at once.
 */
import type { BoardRole } from '../../../../types/taDemand';
import { isDraft } from '../../../../types/taDemand';

/** `2026-04-15` → `15.04.`, `2026-04` → `04.2026`. The board is German-facing. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const p = iso.split('-');
  return p.length >= 3 ? `${p[2]}.${p[1]}.` : `${p[1]}.${p[0]}`;
}

/** IC0–M7, small and monospaced so the column reads as a ruler down the page. */
export function LevelBadge({ level }: { level: string }) {
  if (!level) return null;
  return (
    <span className="shrink-0 rounded-sm border border-gray-200 px-1 font-mono text-[10px] leading-4 text-gray-500">
      {level}
    </span>
  );
}

/**
 * New seat or backfill, as ONE glyph.
 *
 * `+` is a seat that did not exist before; `↻` is one being refilled, and the
 * name it replaces is the tooltip — worth having, and never worth a column of
 * its own (it was the widest thing on the demand board and pushed the role
 * titles, which every row is actually about, into an ellipsis).
 */
export function ReasonGlyph({ role }: { role: BoardRole }) {
  const backfill = role.reason === 'backfill';
  const who = !isDraft(role) ? role.backfillFor : '';
  const help = backfill
    ? who
      ? `Backfill for ${who}`
      : 'Backfill — the HDB does not say whose seat'
    : 'New seat';
  return (
    <span
      title={help}
      aria-label={help}
      className={`shrink-0 cursor-help text-[12px] leading-4 ${backfill ? 'text-violet-500' : 'text-gray-300'}`}
    >
      {backfill ? '↻' : '+'}
    </span>
  );
}

/**
 * The whole seat, on hover. Both sides at once: what was promised, what
 * happened, and who it became.
 *
 * Rendered in normal flow and shown on group-hover — never a portal. The board
 * scrolls in two directions inside its own container, and a portalled popover
 * has to chase that; one that sits in the row cannot drift away from the thing
 * it describes.
 *
 * ⚠️ IT HANGS OFF THE FROZEN COLUMN, not off the row — see `HiringRow`. The row
 * is as wide as every month, so anchoring to it put the card off-screen to the
 * left as soon as the board was scrolled right.
 */
export function RoleHoverCard({ role }: { role: BoardRole }) {
  if (isDraft(role)) return null;
  const hire = role.hire;
  const lines: [string, string][] = [];
  if (hire?.person?.position && hire.person.position !== role.title) {
    lines.push(['Now', hire.person.position]);
  }
  if (role.level) lines.push(['Level', role.level]);
  if (role.reason === 'backfill') {
    lines.push(['Backfill for', role.backfillFor || 'not recorded']);
  }
  lines.push(['Planned', role.plannedMonth || 'no date']);
  if (role.adjustedMonth && role.adjustedMonth !== role.plannedMonth) {
    lines.push(['Re-planned', role.adjustedMonth]);
  }
  if (role.offerAcceptedMonth) lines.push(['Offer accepted', role.offerAcceptedMonth]);
  if (role.actualStartDate) lines.push(['Started', role.actualStartDate]);
  else if (role.actualMonth) lines.push(['Started', role.actualMonth]);
  if (role.recruiter) lines.push(['Recruiter', role.recruiter]);

  return (
    <div
      data-testid="role-hover-card"
      className="pointer-events-none absolute left-2 top-full z-20 hidden w-64 rounded-md border border-gray-200 bg-white p-2 shadow-lg group-hover:block"
    >
      <div className="text-[12px] font-medium text-gray-900">{hire?.name || role.title}</div>
      {hire?.name && <div className="text-[11px] text-gray-500">{role.title}</div>}
      <dl className="mt-1 space-y-0.5">
        {lines.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2 text-[11px]">
            <dt className="shrink-0 text-gray-400">{k}</dt>
            <dd className="min-w-0 truncate text-gray-700">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
